import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';

const UsersList = forwardRef(({ onSelectUser, selectedUserId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchUsers
  }));

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
    fetchUsers();
  }, [roleFilter, statusFilter]);

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/departments/'));
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/roles/'));
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object responses
        const usersList = Array.isArray(data) ? data : (data.users || []);
        setUsers(usersList);
      } else {
        setError(t('users.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(t('users.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const formatRole = (role, roleId) => {
    // If we have a role_id, use the role name from the roles list
    if (roleId && roles.length > 0) {
      const roleObj = roles.find(r => r.role_id === roleId);
      if (roleObj) return roleObj.role_name;
    }
    // Fallback to legacy role mapping
    const roleMap = {
      'ADMIN': t('users.filters.roles.administrator'),
      'MANAGER': t('users.filters.roles.manager'),
      'SUPERVISOR': t('users.filters.roles.supervisor'),
      'OPERATOR': t('users.filters.roles.operator'),
      'USER': t('users.filters.roles.user'),
      'READONLY': t('users.filters.roles.readonly')
    };
    return roleMap[role] || t('users.filters.roles.user');
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId || departments.length === 0) return '-';
    const dept = departments.find(d => d.department_id === departmentId);
    return dept ? dept.department_name : '-';
  };

  // Every role of the user (#285), primary first: [[code, role_id], ...]
  const userRoles = (user) => {
    const codes = user.roles?.length ? user.roles : [user.role];
    const ids = user.role_ids?.length ? user.role_ids : [user.role_id];
    return codes.map((code, i) => [code, ids[i]]);
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();

    const matchesSearch = (fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.username?.toLowerCase().includes(searchTerm.toLowerCase()));

    // Compare role codes (data), not translated labels (#356)
    const matchesRole = roleFilter === 'all' || userRoles(user).some(([code]) => code === roleFilter);

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.is_active) ||
                         (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadge = (role, roleId) => {
    const displayRole = formatRole(role, roleId);
    const roleClass = {
      ADMIN: 'role-admin',
      MANAGER: 'role-manager',
      SUPERVISOR: 'role-supervisor',
      OPERATOR: 'role-operator',
      USER: 'role-user',
      READONLY: 'role-viewer'
    }[role] || 'role-viewer';

    return <span className={`role-badge ${roleClass}`}>{displayRole}</span>;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('users.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('users.filters.status.inactive')}</span>
    );
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return <span className="last-login never">{t('users.lastLogin.never')}</span>;

    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    let text = '';
    let className = 'last-login';

    if (diffHours < 1) {
      text = t('users.lastLogin.justNow');
      className += ' recent';
    } else if (diffHours < 24) {
      text = t('users.lastLogin.hoursAgo', { hours: diffHours });
      className += ' recent';
    } else if (diffDays < 7) {
      text = t('users.lastLogin.daysAgo', { days: diffDays });
      className += diffDays < 3 ? ' recent' : '';
    } else if (diffDays < 30) {
      text = t('users.lastLogin.weeksAgo', { weeks: Math.floor(diffDays / 7) });
      className += ' old';
    } else {
      text = formatAppDate(date);
      className += ' old';
    }

    return <span className={className}>{text}</span>;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && users.length === 0) {
    return <div className="list-container"><div className="loading">{t('users.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('users.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('users.newUser')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('users.filters.allRoles')}</option>
            {roles.map(r => (
              <option key={r.role_id} value={r.role_code}>{r.role_name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('users.filters.allStatus')}</option>
            <option value="active">{t('users.filters.status.active')}</option>
            <option value="inactive">{t('users.filters.status.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('users.tableHeaders.user')}</th>
              <th>{t('users.tableHeaders.role')}</th>
              <th>{t('users.tableHeaders.department')}</th>
              <th>{t('users.tableHeaders.email')}</th>
              <th>{t('users.tableHeaders.lastLogin')}</th>
              <th>{t('users.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? t('users.noData.searching') : t('users.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => {
                const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();

                return (
                  <tr
                    key={user.user_id}
                    onClick={() => onSelectUser(user.user_id)}
                    className={selectedUserId === user.user_id ? 'selected' : ''}
                  >
                    <td>
                      <div className="user-info">
                        <div className="user-name">
                          <strong>{fullName || user.username || t('users.unnamedUser')}</strong>
                        </div>
                        <div className="user-details">
                          <span className="user-username">@{user.username}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {userRoles(user).map(([code, id]) => (
                        <React.Fragment key={id || code}>{getRoleBadge(code, id)} </React.Fragment>
                      ))}
                    </td>
                    <td>
                      <span className="department-name">{getDepartmentName(user.department_id)}</span>
                    </td>
                    <td>
                      <span className="user-email">{user.email || '-'}</span>
                    </td>
                    <td>
                      {formatLastLogin(user.last_login)}
                    </td>
                    <td>
                      {getStatusBadge(user.is_active)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('users.recordCount', { filtered: filteredUsers.length, total: users.length })}
        </div>
      </div>
    </div>
  );
});

UsersList.displayName = 'UsersList';

export default UsersList;